import { z } from "zod";

import { MealTypeSchema } from "./meal";

// One ingredient line that a recipe needs. `unit` is free-form here (LLM uses
// natural Polish words like "kromki", "ząbek", "garść"); the pantry/shopping
// side normalises to its strict enum at insert time.
export const RecipeIngredientSchema = z.object({
  name: z.string().min(1).max(200),
  quantity: z.number().min(0).max(10000).nullable(),
  unit: z.string().max(20).nullable(),
});
export type RecipeIngredient = z.infer<typeof RecipeIngredientSchema>;

// Single recipe suggestion produced by the LLM.
export const RecipeSuggestionSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().min(1).max(500),
  mealType: MealTypeSchema.nullable(),
  estimatedKcal: z.number().int().min(0).max(10000),
  estimatedProteinG: z.number().min(0).max(500).nullable(),
  estimatedFatG: z.number().min(0).max(500).nullable(),
  estimatedCarbsG: z.number().min(0).max(1000).nullable(),
  uses: z.array(RecipeIngredientSchema).max(20),
  missing: z.array(RecipeIngredientSchema).max(20),
  instructions: z.string().min(1).max(1000),
  confidence: z.number().min(0).max(1),
});
export type RecipeSuggestion = z.infer<typeof RecipeSuggestionSchema>;

export const RecipeSuggestionsResponseSchema = z.object({
  recipes: z.array(RecipeSuggestionSchema).max(5),
});
export type RecipeSuggestionsResponse = z.infer<typeof RecipeSuggestionsResponseSchema>;

// Input for POST /pantry/recipes.
export const RecipeRequestSchema = z.object({
  mealType: MealTypeSchema.optional(),
  kcalBudget: z.number().int().min(50).max(5000).optional(),
});
export type RecipeRequest = z.infer<typeof RecipeRequestSchema>;

// Input for POST /pantry/craving. User types what they feel like eating,
// LLM proposes a recipe that fits their craving + remaining daily budget.
export const CravingRequestSchema = z.object({
  craving: z.string().trim().min(2).max(200),
  pantryOnly: z.boolean().optional(),
  hourLocal: z.number().int().min(0).max(23).optional(),
  // Client getTimezoneOffset() — so "meals eaten today" is bucketed by the
  // user's local day, not the UTC day.
  tzOffset: z.number().int().min(-840).max(840).optional(),
});
export type CravingRequest = z.infer<typeof CravingRequestSchema>;
